import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { useMutation, useQuery } from '@/utils/api';

export interface MasterKeyInfo {
  plugin: string;
  configured: boolean;
  maskedKey: string;
}

export function useMasterKey(plugin: string, enabled = true) {
  const axios = useAxios({ doNotImpersonate: true });

  return useQuery<MasterKeyInfo>({
    defaultErrorMessage: 'Failed to fetch master key info',
    queryKey: ['enrichment', plugin, 'key'],
    enabled: enabled && !!plugin,
    queryFn: async () => {
      const { data } = await axios.get(`/enrichment/${plugin}/key`);
      return data;
    },
  });
}

interface SetMasterKeyParams {
  plugin: string;
  apiKey: string;
  extra?: Record<string, string>;
}

export function useDeleteMasterKey() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<MasterKeyInfo, Error, { plugin: string }>({
    defaultErrorMessage: 'Failed to delete master key',
    mutationFn: async ({ plugin }) => {
      const { data } = await axios.delete(`/enrichment/${plugin}/key`);
      return data;
    },
    onSuccess: (_data, variables) => {
      toast.success(`Master key removed for ${variables.plugin}`);
      queryClient.invalidateQueries({
        queryKey: ['enrichment', variables.plugin, 'key'],
      });
      queryClient.invalidateQueries({
        queryKey: ['enrichment', 'plugins'],
      });
    },
  });
}

export function useSetMasterKey() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<MasterKeyInfo, Error, SetMasterKeyParams>({
    defaultErrorMessage: 'Failed to set master key',
    mutationFn: async ({ plugin, apiKey, extra }) => {
      const { data } = await axios.put(`/enrichment/${plugin}/key`, {
        apiKey,
        extra,
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      toast.success(`Master key updated for ${variables.plugin}`);
      queryClient.invalidateQueries({
        queryKey: ['enrichment', variables.plugin, 'key'],
      });
      queryClient.invalidateQueries({
        queryKey: ['enrichment', 'plugins'],
      });
    },
  });
}
